import React, { useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import moment from 'moment';

import { imageFix } from '../../../helpers';
import OfferInstance from '../OfferSlots/OfferInstance';
import ApplyOfferInstance from '../OfferSlots/ApplyOfferInstance';
import OfferDateBox from './OfferDateBox';
import { vegan, group as groupIcon, gift, anywhere, paid } from './icons';
import { Row, Col } from '../../Grid';
import ReservationSlots from '../OfferSlots/ReservationSlot';
import ReservationBucket from '../OfferSlots/ReservationBucket';
import GroupOfferView from '../OfferSlots/GroupOfferView';
import OfferName from './OfferName';
import CancelOffer from './CancelOffer';
import { CancelButton, CanceledButton } from '../OfferSlots/Common';
import './Offer.css';
import mustPost from '../../../svg/must_post.svg';

const Title = styled.h2`
  color: #555555;
  font-size: 22px;
  margin-bottom: 10px;
  padding-right: 60px;
`;

const BrandName = styled.h3`
  font-family: BrandonGrotesque-Light;
  color: #555555;
  font-size: 15px;
  font-weight: 300;
  margin: 10px 0;
`;

const Description = styled.p`
  min-height: 30px;
  max-height: 60px;
  padding-right: 10px;
  overflow: hidden;
  font-size: 14px;
  width: 80%;
  color: #555555;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
`;

const Box = styled.div`
  flex: 1;
  font-family: BrandonGrotesque-Regular;
  margin-left: 30px;
  position: relative;
`;

const RaggedBottom = styled.div`
  background: linear-gradient(45deg, var(--color-beige) 9px, transparent 0),
    linear-gradient(315deg, var(--color-beige) 9px, transparent 0);
  background-repeat: repeat-x;
  background-size: 14px 22px;
  content: ' ';
  width: 100%;
  height: 20px;
`;

const OfferImage = styled.img`
  width: 240px;
  height: 138px;
  object-fit: cover;
`;

const OfferIcons = styled.div`
  margin-top: 7px;
`;

const OfferContainer = styled.div`
  background-color: #fafafa;
  width: 100%;
  padding: 10px;
  margin-bottom: 20px;
  position: relative;
`;

const GroupInfo = styled.div`
  font-family: BrandonGrotesque-Regular;
  font-size: 14px;
  line-height: 1.29;
  letter-spacing: normal;
  color: #333333;
`;

const CancelContainer = styled.div`
  position: absolute;
  bottom: 10px;
  right: 0px;
`;

const HideSchedule = styled.div`
  font-family: BrandonGrotesque-Regular;
  font-size: 10px;
  line-height: 1.8;
  text-align: right;
  color: #999999;
  text-decoration: underline;
  cursor: pointer;
`;

const DiscountBanner = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 80px;
  height: 17px;
  background-color: var(--color-khaki);
  font-family: BrandonGrotesque-Regular;
  font-size: 11px;
  font-weight: 500;
  font-style: italic;
  line-height: 21px;
  padding-left: 10px;
  color: #ffffff;
`;

const PendingBanner = styled.div`
  position: absolute;
  right: 0px;
  bottom: 0;
  padding: 5px 12px 2px 12px;

  background-color: #555555;
  font-family: BrandonGrotesque-Regular;
  font-size: 13px;
  font-weight: 500;
  font-style: italic;
  line-height: 21px;
  color: #ffffff;
`;

const RRPendingBanner = styled.div`
  padding: 5px 12px 2px 12px;
  line-height: 21px;
  background-color: #555555;
  font-family: BrandonGrotesque-Regular;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-style: italic;
  color: #ffffff;
`;

const RRBanner = styled.div`
  font-family: BrandonGrotesque-Medium;
  font-size: 14px;
  font-weight: bold;
  font-style: italic;
  color: #994f4f;
`;

interface Props {
  group: OfferGroup;
  type: string;
}

declare interface LolProps {
  slots: OfferSlots;
  type: string;
  date: string;
  offer: Offer;
}

function Lol({ slots, type, date, offer }: LolProps) {
  switch (slots.type) {
    case 'reservation_slot':
      return <ReservationBucket offer={slots.offer} type={type} />;
    case 'reservation_offer':
      return (
        <>
          {slots.attendees.length && <RaggedBottom />}
          {slots.attendees
            .filter((att) => att.reserved_for || type === 'past')
            .map((attendee, idx) => (
              <ReservationSlots
                attendee={attendee}
                type={type}
                offerId={slots.offerId}
                date={date}
                key={idx}
              />
            ))}
        </>
      );
    case 'normal':
      return (
        <>
          {slots.offers.length && <RaggedBottom />}
          {slots.offers.map((offer, idx) => {
            if (offer.mode === 'apply' && offer.deliberation_status !== 'finalized') {
              return <ApplyOfferInstance offer={offer} type={type} key={idx} />;
            }
            return <OfferInstance offer={offer} type={type} key={idx} />;
          })}
        </>
      );
    case 'group_offer':
      return slots.offer.groups ? (
        <>
          {slots.offer.groups.length && <RaggedBottom />}
          {slots.offer.groups.map((group) => (
            <GroupOfferView type={type} group={group} key={group.id} offerId={slots.offer.id} />
          ))}
        </>
      ) : (
        <></>
      );
    default:
      return null;
  }
}

function OfferView({ group, type }: Props) {
  (window as any).moment = moment;
  let first = group.offer;
  let description =
    first.description && first.description.length > 200
      ? first.description.substring(0, 200).trim() + '...'
      : first.description;

  description = description ?? '';
  description = description.replace('<p>', '');
  description = description.replace('</p>', '<br/>');
  const [showSchedule, changeSchedule] = useState(true);

  let boxDate = first.starts_at;

  if (first.type === 'job') {
    if (first.deal_call_sheet_ready === false && first.display_start_date) {
      boxDate = first.display_start_date;
    } else if (first.deal_call_sheet_ready === true) {
      let earliest;

      first.attendees.forEach((attendee) => {
        if (
          attendee.call_sheet_status === 'ready' &&
          attendee.call_time !== undefined &&
          (earliest === undefined || earliest.isAfter(moment(attendee.call_time)))
        ) {
          earliest = moment(attendee.call_time);
        }
      });

      if (earliest !== undefined) {
        boxDate = earliest;
      }
    }
  }

  if (first.type === 'reservation_required')
    return (
      <OfferContainer>
        <Row>
          <Col size="0 0 240px">
            <div style={{ position: 'relative' }}>
              <OfferImage
                src={imageFix(
                  first.media
                    ? first.media.reduce((min, image) => (min.sort < image.sort ? min : image), {
                        thumbnail: '',
                        sort: 1000,
                      }).thumbnail
                    : ''
                )}
                alt={first.title}
              />
            </div>
          </Col>
          <Box>
            <Title>{first.subtitle}</Title>
            <BrandName>{first.title.toUpperCase()}</BrandName>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </Box>
        </Row>
        <Row>
          <div
            style={{
              padding: '20px 10px 0px 10px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <RRBanner>Reservation Required</RRBanner>
            {first.state === 'pending' && <RRPendingBanner>PENDING</RRPendingBanner>}
          </div>
        </Row>
        {first.mode === 'apply' &&
          (first.deliberation_status !== 'finalized' || type !== 'upcoming') && (
            <Lol slots={group.parts} type={type} date={first.starts_at} offer={group.offer} />
          )}
      </OfferContainer>
    );

  if (type === 'upcoming' && !moment(group.offer.starts_at).isAfter(moment(), 'day')) {
    return null;
  }

  return (
    <OfferContainer>
      <Row>
        <Col size="0 0 240px">
          <div style={{ position: 'relative' }}>
            <OfferImage
              src={imageFix(
                first.media
                  ? first.media.reduce((min, image) => (min.sort < image.sort ? min : image), {
                      thumbnail: '',
                      sort: 1000,
                    }).thumbnail
                  : ''
              )}
              alt={first.title}
            />
            {first.discount && (
              <DiscountBanner>
                <b>DISCOUNT</b>
              </DiscountBanner>
            )}
          </div>
        </Col>
        <Box>
          <Title>{first.subtitle}</Title>
          <BrandName>{first.title.toUpperCase()}</BrandName>
          <Description dangerouslySetInnerHTML={{ __html: description }} />
          <OfferIcons>
            {first.is_group ? groupIcon : null}
            {first.is_vegan ? vegan : null}
            {first.type === 'must_post' ? (
              <img src={mustPost} width="16" height="16" alt="" />
            ) : null}
            {first.type === 'gift' ? gift : null}
            {first.has_no_location && ['gift', 'job'].indexOf(first.type) === -1 ? anywhere : null}
            {first.wages || first.type === 'job' ? paid : null}
          </OfferIcons>
          {group.parts.type === 'group_offer' && (
            <>
              <OfferName offer={group.offer} main />
              <GroupInfo>
                <b>
                  {group.offer.taken_spots}/
                  {group.offer.available_spots === -1 ? '∞' : group.offer.available_spots}
                </b>{' '}
                Claims |{' '}
                <b>
                  {group.offer.groups
                    ? group.offer.groups.filter((g) => g.status !== 'cancelled').length
                    : 0}
                  /{group.offer.group_max_number}
                </b>{' '}
                Groups
              </GroupInfo>
            </>
          )}
          {(group.parts.type === 'group_offer' || group.parts.type === 'reservation_slot') &&
            type !== 'past' &&
            !group.offer.canceled_at && (
              <CancelOffer offer={group.offer}>
                <CancelContainer>
                  <CancelButton invert />
                </CancelContainer>
              </CancelOffer>
            )}
          {(group.parts.type === 'group_offer' || group.parts.type === 'reservation_slot') &&
            group.offer.canceled_at && (
              <CanceledButton style={{ float: 'right', marginRight: '24px' }}>
                CANCELED
              </CanceledButton>
            )}
          {group.parts.type === 'reservation_offer' && (
            <HideSchedule onClick={() => changeSchedule(!showSchedule)}>Hide Schedule</HideSchedule>
          )}{' '}
          {first.state === 'pending' || first.verification_status === 'unverified' ? (
            <PendingBanner>
              <b>PENDING</b>
            </PendingBanner>
          ) : null}
        </Box>
      </Row>
      {group.offer.type !== 'gift' ? <OfferDateBox date={boxDate} /> : null}
      {showSchedule && (
        <Lol slots={group.parts} type={type} date={first.starts_at} offer={group.offer} />
      )}
    </OfferContainer>
  );
}

export default connect(null, {})(OfferView);
